import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, Receipt, Truck, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate, slug } from "@/lib/format";
import { BillDialog } from "@/components/finance/BillDialog";
import { BulkBillDialog } from "@/components/finance/BulkBillDialog";

function DoCell({ r }) {
  if (!r.shipments.length) {
    return <small className="fin-muted">{r.source === "odoo_import" ? "Dikirim via Odoo" : "—"}{r.delivered_at ? ` · ${fmtDate(r.delivered_at)}` : ""}</small>;
  }
  return (
    <div className="fin-do-list">
      {r.shipments.map((s) => <span key={s.id}><Truck size={11} /> {s.picking} · {fmtDate(s.date)}</span>)}
    </div>
  );
}

function BulkBar({ picked, onBill, onClear }) {
  if (!picked.length) return null;
  return (
    <div className="bulk-bar" data-testid="bulk-bar">
      <span><b data-testid="bulk-selected-count">{picked.length}</b> order dipilih · {new Set(picked.map((r) => r.customer_odoo_id)).size} customer · estimasi <b>{rupiah(picked.reduce((s, r) => s + r.estimate.amount, 0))}</b></span>
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={onClear} data-testid="bulk-clear-btn"><X size={14} /> Batal pilih</button>
      <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={onBill} data-testid="bulk-bill-btn"><Receipt size={14} /> Tagihkan Terpilih</button>
    </div>
  );
}

export function BillableTab({ refreshKey, onBilled }) {
  const { hasPerm } = useAuth();
  const canBill = hasPerm("invoices:create");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [billFor, setBillFor] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    api.get("/finance/billable", { params: { q: q || undefined } })
      .then((r) => { setRows(r.data.data); setSel(new Set()); }).catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  const picked = (rows || []).filter((r) => sel.has(r.sales_id));
  const allOn = !!rows?.length && picked.length === rows.length;
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allOn ? new Set() : new Set(rows.map((r) => r.sales_id)));
  const done = () => { setBillFor(null); setBulkOpen(false); load(); onBilled(); };

  return (
    <div className="panel table-panel" data-testid="billable-tab">
      <div className="table-tools">
        <div className="search-box"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari SO / outlet…" data-testid="billable-search" /></div>
        <p className="fin-hint">Order yang sudah dikirim penuh atau sebagian dan masih ada qty yang belum ditagih. Centang beberapa order untuk ditagih sekaligus.</p>
      </div>
      {canBill && <BulkBar picked={picked} onBill={() => setBulkOpen(true)} onClear={() => setSel(new Set())} />}
      {rows === null ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : rows.length === 0 ? (
        <div className="rj-empty" data-testid="billable-empty"><CheckCircle2 /><p>Semua pengiriman sudah ditagih.</p></div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="fin-table">
            <thead><tr>
              {canBill && <th className="chk-col"><input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="Centang semua" data-testid="billable-select-all" /></th>}
              <th>SO / Outlet</th><th>Pengiriman (DO)</th><th>Qty belum ditagih</th><th>Estimasi tagihan</th><th>AR Term</th><th style={{ textAlign: "right" }}>Aksi</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.sales_id} className={sel.has(r.sales_id) ? "row-picked" : ""} data-testid={`billable-row-${r.so_number}`}>
                  {canBill && <td className="chk-col"><input type="checkbox" checked={sel.has(r.sales_id)} onChange={() => toggle(r.sales_id)} aria-label={`Pilih ${r.so_number}`} data-testid={`billable-check-${r.so_number}`} /></td>}
                  <td><strong>{r.outlet}</strong><span className="odoo-ref">{r.so_number}</span>
                    {r.billing_status === "partial" && <span className="bill-chip partial">Ditagih sebagian</span>}</td>
                  <td><span className={`order-status ${slug(r.status)}`}>{r.status}</span><DoCell r={r} /></td>
                  <td><b>{r.to_bill_qty}</b> <small className="fin-muted">unit · {r.lines.filter((l) => l.to_bill_qty > 0).length} produk</small></td>
                  <td><b>{rupiah(r.estimate.amount)}</b></td>
                  <td><small data-testid={`billable-ar-term-${r.so_number}`}>{!r.customer_in_app ? "Termin Odoo" : (r.ar_term_name || <span className="term-missing">Belum diatur</span>)}{r.customer_in_app && r.ar_term_days && !r.ar_term_lines?.some((l) => l.delay_type && l.delay_type !== "days_after") ? ` · ${r.ar_term_days} hari` : ""}</small></td>
                  <td style={{ textAlign: "right" }}>
                    {canBill && (
                      <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={() => setBillFor(r)} data-testid={`bill-btn-${r.so_number}`}>
                        <Receipt size={14} /> Tagihkan
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {billFor && <BillDialog row={billFor} onClose={() => setBillFor(null)} onSaved={done} />}
      {bulkOpen && <BulkBillDialog rows={picked} onClose={() => setBulkOpen(false)} onSaved={done} />}
    </div>
  );
}
